import type { SmartFormConfig } from '@pesto/ui/features/SmartForm/types';

import { Button } from '@pesto/ui';
import { CurrencyField } from '@pesto/ui/components/Forms/CurrencyField';
import { DobField } from '@pesto/ui/components/Forms/DobField';
import { Modal } from '@pesto/ui/components/Modal';
import { PhoneInput } from '@pesto/ui/components/PhoneInput';
import { SmartForm } from '@pesto/ui/features/SmartForm/SmartForm';
import { getCurrentDate, toSamplePhone } from '@pesto/utils';
import { STATES } from '@pesto/utils/constants/states';
import { toDobIso } from '@pesto/utils/src/dateHelper';
import { useMemo } from 'react';
import { z } from 'zod';

import type { OnboardNewUserInput } from '../../../__generated__/graphql/api';

import { CardProcessor, useListProgramsQuery, useOnboardNewUserMutation } from '../../../__generated__/graphql/api';
import { IS_PROD } from '../../../constants/global-env';

const schema = z
  .object({
    programId: z.string().min(1, 'Program is required'),
    email: z.string().email('Enter a valid email'),
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    phone: z.string().min(1, 'Phone is required'),
    dob: z.string().regex(/^\d{2}-\d{2}-\d{4}$/, 'Date of birth must be DD-MM-YYYY'),
    ssn: z.string().min(9, 'SSN is required'),
    addressFirstLine: z.string().min(1, 'Address line 1 is required'),
    addressSecondLine: z.string().optional(),
    city: z.string().min(1, 'City is required'),
    state: z.string().min(1, 'State is required'),
    zip: z.string().min(1, 'ZIP is required'),
    creditAmount: z.coerce.number().int().positive('Credit amount is required'),
    annualIncome: z.coerce.number().int().positive('Annual income is required'),
    partnerAccountNumber: z.string().min(1, 'Partner account number is required'),
    originationDate: z.string().min(1, 'Origination date is required'),
    issueCard: z.boolean(),
    cardProcessor: z.nativeEnum(CardProcessor),
    initialBalance: z.coerce.number().int().min(0).optional(),
    promoEndDate: z.string().optional(),
    aprMargin: z.string().min(1, 'APR margin is required'),
    initialApr: z.string().min(1, 'Initial APR is required'),
  })
  .superRefine((values, ctx) => {
    if (!values.issueCard && !(values.initialBalance && values.initialBalance > 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['initialBalance'],
        message: 'Cardless boarding requires a positive opening balance',
      });
    }
  });

type OnboardNewUserFormValues = z.infer<typeof schema>;

type OnboardNewUserModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onCompleted: (creditApplicationId: string) => void;
};

const CARD_PROCESSOR_OPTIONS = [
  { label: 'I2C', value: CardProcessor.I2C },
  { label: 'Lithic', value: CardProcessor.Lithic },
];

const optionalString = (value?: string) => {
  const trimmed = value?.trim();

  return trimmed ? trimmed : undefined;
};

const buildEmptyValues = (programId = ''): OnboardNewUserFormValues => ({
  programId,
  email: '',
  firstName: '',
  lastName: '',
  phone: '',
  dob: '',
  ssn: '',
  addressFirstLine: '',
  addressSecondLine: '',
  city: '',
  state: '',
  zip: '',
  creditAmount: 0,
  annualIncome: 0,
  partnerAccountNumber: '',
  originationDate: getCurrentDate(),
  issueCard: true,
  cardProcessor: CardProcessor.I2C,
  initialBalance: 0,
  promoEndDate: '',
  aprMargin: '',
  initialApr: '',
});

const buildSampleValues = (programId = ''): OnboardNewUserFormValues => {
  const suffix = String(Date.now()).slice(-7);

  return {
    programId,
    email: `mission-control-${suffix}@example.com`,
    firstName: 'Mission',
    lastName: `User${suffix.slice(-3)}`,
    phone: toSamplePhone(suffix),
    dob: '15-01-1990',
    ssn: '123456789',
    addressFirstLine: '123 Test Street',
    addressSecondLine: '',
    city: 'Austin',
    state: 'TX',
    zip: '78701',
    creditAmount: 500_000,
    annualIncome: 7_500_000,
    partnerAccountNumber: `HL-MC-${suffix}`,
    originationDate: getCurrentDate(),
    issueCard: true,
    cardProcessor: CardProcessor.I2C,
    initialBalance: 0,
    promoEndDate: '',
    aprMargin: '2.6250',
    initialApr: '11.5000',
  };
};

const toInput = (values: OnboardNewUserFormValues): OnboardNewUserInput => {
  const initialBalance = values.initialBalance ?? 0;

  const input: OnboardNewUserInput = {
    programId: values.programId,
    email: values.email,
    firstName: values.firstName,
    lastName: values.lastName,
    phone: values.phone,
    dob: toDobIso(values.dob),
    ssn: values.ssn,
    addressInput: {
      addressFirstLine: values.addressFirstLine,
      addressSecondLine: optionalString(values.addressSecondLine),
      city: values.city,
      state: values.state,
      zip: values.zip,
    },
    creditAmount: values.creditAmount,
    annualIncome: values.annualIncome,
    partnerAccountNumber: values.partnerAccountNumber,
    originationDate: values.originationDate,
    issueCard: values.issueCard,
    cardProcessor: values.cardProcessor,
    aprMargin: values.aprMargin,
    initialApr: values.initialApr,
  };

  if (initialBalance > 0) input.initialBalance = initialBalance;

  if (optionalString(values.promoEndDate)) input.promoEndDate = values.promoEndDate;

  return input;
};

export const OnboardNewUserModal = ({ isOpen, onClose, onCompleted }: OnboardNewUserModalProps) => {
  const { data: programsData, loading: programsLoading } = useListProgramsQuery({ skip: !isOpen });
  const [onboardNewUser, { loading, error }] = useOnboardNewUserMutation();

  const programOptions = useMemo(
    () =>
      (programsData?.programs ?? []).flatMap((program) =>
        program?.id
          ? [
              {
                label: `${program.name ?? 'Unnamed program'} (${program.cardProgram ?? 'unknown card program'})`,
                value: program.id,
              },
            ]
          : [],
      ),
    [programsData?.programs],
  );

  const defaultProgramId = programOptions[0]?.value ?? '';
  const buildDefaultValues = IS_PROD ? buildEmptyValues : buildSampleValues;
  const defaultValues = useMemo(() => buildDefaultValues(defaultProgramId), [buildDefaultValues, defaultProgramId]);

  const config = useMemo<SmartFormConfig<OnboardNewUserFormValues>>(
    () => ({
      schema,
      defaultValues,
      submitLabel: 'Onboard user',
      layout: 'grid',
      columns: 2,
      fieldGroups: [
        {
          name: 'program',
          label: 'Program',
          fields: [
            {
              name: 'programId',
              label: 'Program',
              type: 'select',
              options: programOptions,
              required: true,
              disabled: programsLoading,
            },
          ],
        },
        {
          name: 'person',
          label: 'Person',
          fields: [
            { name: 'email', label: 'Email', type: 'email', required: true },
            { name: 'firstName', label: 'First Name', type: 'text', required: true },
            { name: 'lastName', label: 'Last Name', type: 'text', required: true },
            { name: 'phone', label: 'Phone', component: PhoneInput, required: true },
            {
              name: 'dob',
              label: 'Date of Birth',
              component: DobField,
              required: true,
              placeholder: 'DD-MM-YYYY',
              helperText: 'DD-MM-YYYY',
            },
            { name: 'ssn', label: 'SSN', type: 'text', required: true },
          ],
        },
        {
          name: 'address',
          label: 'Address',
          fields: [
            { name: 'addressFirstLine', label: 'Address Line 1', type: 'text', required: true },
            { name: 'addressSecondLine', label: 'Address Line 2', type: 'text' },
            { name: 'city', label: 'City', type: 'text', required: true },
            { name: 'state', label: 'State', type: 'select', options: STATES, required: true },
            { name: 'zip', label: 'ZIP', type: 'text', required: true },
          ],
        },
        {
          name: 'application',
          label: 'Application',
          fields: [
            {
              name: 'creditAmount',
              label: 'Credit Amount',
              component: CurrencyField,
              componentProps: { showCurrencySymbol: true },
              required: true,
            },
            {
              name: 'annualIncome',
              label: 'Annual Income',
              component: CurrencyField,
              componentProps: { showCurrencySymbol: true },
              required: true,
            },
            { name: 'partnerAccountNumber', label: 'Partner Account Number', type: 'text', required: true },
            { name: 'originationDate', label: 'Origination Date', type: 'date', required: true },
            {
              name: 'issueCard',
              label: 'Issue Card',
              type: 'boolean',
              helperText: 'Turn off to board a cardless BBYS/opening-balance user.',
            },
            {
              name: 'cardProcessor',
              label: 'Card Processor',
              type: 'select',
              options: CARD_PROCESSOR_OPTIONS,
              conditional: { when: { field: 'issueCard', operator: 'truthy' } },
            },
            {
              name: 'initialBalance',
              label: 'Opening Balance',
              component: CurrencyField,
              componentProps: { showCurrencySymbol: true, allowZero: true },
              helperText: 'Required (positive) when no card is issued.',
            },
            {
              name: 'promoEndDate',
              label: 'Promo End Date',
              type: 'date',
              helperText: 'Optional 0% promo end date, 14 days to 6 months after origination.',
            },
            { name: 'aprMargin', label: 'APR Margin', type: 'text', required: true },
            { name: 'initialApr', label: 'Initial APR', type: 'text', required: true },
          ],
        },
      ],
    }),
    [defaultValues, programOptions, programsLoading],
  );

  const handleSubmit = async (values: OnboardNewUserFormValues) => {
    try {
      const result = await onboardNewUser({ variables: { input: toInput(values) } });
      const creditApplicationId = result.data?.onboardNewUser?.id;

      if (creditApplicationId) {
        onCompleted(creditApplicationId);
      }
    } catch {}
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Onboard new user"
      description="Creates a credit application through Alfredo's partner boarding path and publishes approved users to Starlink."
      className="sm:max-w-4xl"
    >
      <SmartForm
        config={config}
        onSubmit={handleSubmit}
        loading={loading || programsLoading}
        formError={error?.message}
        showResetButton
        resetLabel={IS_PROD ? 'Reset' : 'Reset sample'}
      >
        {(form) =>
          !IS_PROD && (
            <div className="mb-4 flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => form.reset(buildSampleValues(form.getValues('programId') || defaultProgramId))}
              >
                Fill sample data
              </Button>
            </div>
          )
        }
      </SmartForm>
    </Modal>
  );
};
